import React, { useEffect, useState, useCallback } from "react";
import api, { apiError, openInvoicePdf } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import { money, fmtDate, DOC_STATUS, missionLabel, PAYMENT_METHODS, paymentLabel, todayIn } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Send, FileText, Check, MessageCircle, Download, Pencil, LayoutTemplate, Star, BellRing, Image as ImageIcon, Upload, Loader2 } from "lucide-react";
import ChatImage from "@/components/ChatImage";
import ToInvoice from "@/components/ToInvoice";
import { cn } from "@/lib/utils";
import { rideLineLabel, rideVatRate } from "@/lib/ride";
import { toast } from "sonner";

// Validité d'un devis : 30 jours après l'émission par défaut (même règle que le serveur).
const QUOTE_VALIDITY_DAYS = 30;
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (day, n) => { const [y, m, d] = day.split("-").map(Number); return isoDay(new Date(y, m - 1, d + n)); };
const frDay = (day) => day.split("-").reverse().join("/");
// Ancien devis sans date : émission + 30 jours, seulement pour l'affichage (la base n'est pas modifiée).
const quoteValidUntil = (d) => (d.due_date || "").slice(0, 10) || (d.created_at ? addDays(d.created_at.slice(0, 10), QUOTE_VALIDITY_DAYS) : "");

export default function Invoices() {
  const { settings } = useSettings();
  const transport = useVocab().business === "transport"; // onglet « À facturer » (lot T6), transport seulement
  const cur = settings?.currency || "EUR";
  const franchise = settings?.vat_regime !== "assujetti"; // repli franchise, comme le backend
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [missions, setMissions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("invoice");
  const [dialog, setDialog] = useState(false);
  const [payDialog, setPayDialog] = useState({ open: false, item: null });
  const [waConfirm, setWaConfirm] = useState(null); // document envoyé par WhatsApp, en attente de confirmation
  const [toDelete, setToDelete] = useState(null); // brouillon ou devis en attente de confirmation
  const [expMonth, setExpMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = useCallback(() => {
    api.get("/invoices").then((r) => setDocs(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/missions").then((r) => setMissions(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const nameOf = (id) => clients.find((c) => c.id === id)?.name || "—";
  const invoicePaidDate = (d) => (d.paid_at || d.sent_at || d.created_at || "").slice(0, 10);
  const filtered = docs.filter((d) => {
    if (d.type !== tab) return false;
    if (tab === "invoice" && d.status === "paid") return invoicePaidDate(d).startsWith(expMonth);
    return true;
  });

  const setStatus = async (id, status) => { await api.patch(`/invoices/${id}/status`, { status }); load(); };
  const markPaid = async (id, paidAt, method, edit) => { await api.patch(`/invoices/${id}/status`, { status: "paid", paid_at: paidAt, payment_method: method }); toast.success(edit ? "Mode de paiement mis à jour" : "Facture marquée payée"); load(); };
  const del = async (id) => { try { await api.delete(`/invoices/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const send = async (id) => { try { await api.post(`/invoices/${id}/send`); toast.success("Envoyé par email avec PDF"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const convert = async (id) => {
    try { await api.post(`/invoices/${id}/convert`); toast.success("Devis converti en facture"); setTab("invoice"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } // ex. mission déjà facturée entre-temps
  };
  const remind = async (id) => { try { await api.post(`/invoices/${id}/remind`); toast.success("Relance envoyée au client"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/invoices/export", { params: { month: expMonth }, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `factures-${expMonth}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export comptable généré");
    } catch { toast.error("Erreur lors de l'export"); }
  };
  const pdf = async (id) => { try { await openInvoicePdf(id); } catch { toast.error("Impossible d'ouvrir le PDF"); } };
  const exportPdf = async () => {
    try {
      const { data } = await api.get("/invoices/export/pdf", { params: { month: expMonth }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success("Export PDF comptable généré");
    } catch { toast.error("Erreur lors de l'export PDF"); }
  };
  const waSend = async (d) => {
    try {
      const { data } = await api.get(`/invoices/${d.id}/share-link`);
      const c = clients.find((x) => x.id === d.client_id);
      const phone = c?.phone?.replace(/[^0-9]/g, "") || "";
      const msg = encodeURIComponent(`Bonjour, voici votre ${d.type === "invoice" ? "facture" : "devis"} ${d.number} d'un montant de ${d.total_ttc.toFixed(2)} ${cur}. Consultez le PDF : ${data.url}`);
      window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
      // Brouillon : au retour de WhatsApp, proposer de le marquer « envoyé » (il ne sera plus modifiable).
      if (d.status === "draft") setWaConfirm(d);
    } catch { toast.error("Erreur"); }
  };
  const markSent = async (d) => {
    try {
      await api.post(`/invoices/${d.id}/mark-sent`);
      toast.success(d.type === "invoice" ? "Facture marquée envoyée" : "Devis marqué envoyé");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      {/* En-tête et boutons passent à la ligne quand la place manque (téléphone, tablette), comme sur Dépenses ;
          sur un grand écran tout tient sur une ligne et rien ne change. */}
      <div className="flex flex-wrap items-center justify-between gap-3 max-md:items-end">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Comptabilité</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Facturation</h1>
        </div>
        {tab !== "templates" && tab !== "to-invoice" && (
          <div className="flex flex-wrap items-center gap-2">
            {tab === "invoice" && (
              <>
                <input type="month" value={expMonth} onChange={(e) => setExpMonth(e.target.value)} className="h-9 px-2.5 rounded-lg border border-border bg-card text-sm" data-testid="export-month" />
                <Button variant="outline" className="gap-2 rounded-full" onClick={exportCsv} data-testid="export-button"><Download className="h-4 w-4" /> CSV</Button>
                <Button variant="outline" className="gap-2 rounded-full" onClick={exportPdf} data-testid="export-pdf-button"><FileText className="h-4 w-4" /> PDF compta</Button>
              </>
            )}
            <Button className="gap-2 rounded-full" onClick={() => setDialog(true)} data-testid="new-invoice-button"><Plus className="h-4 w-4" /> Créer</Button>
          </div>
        )}
      </div>
      {tab === "invoice" && <p className="text-xs text-muted-foreground -mt-4">Le mois filtre les factures payées par leur date de paiement — les brouillons et factures envoyées restent toujours visibles.</p>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="invoice-tabs">
          {transport && <TabsTrigger value="to-invoice" data-testid="tab-to-invoice">À facturer</TabsTrigger>}
          <TabsTrigger value="invoice" data-testid="tab-invoices">Factures</TabsTrigger>
          <TabsTrigger value="quote" data-testid="tab-quotes">Devis</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates"><LayoutTemplate className="h-4 w-4 mr-1.5" />Modèles</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "to-invoice" ? (
        <ToInvoice settings={settings} onCreated={load} />
      ) : tab === "templates" ? (
        <TemplatesManager templates={templates} onChanged={load} />
      ) : (
        <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border">
          {filtered.length === 0 && <p className="p-6 text-sm text-muted-foreground">Aucun document.</p>}
          {filtered.map((d) => (
            <div key={d.id} className="p-4 md:p-5 flex flex-wrap items-center gap-4" data-testid={`invoice-row-${d.id}`}>
              {/* Sur téléphone : numéro et client sur toute la largeur, pastilles et boutons en dessous.
                  Sur ordinateur : le bloc garde 16rem au moins ; une ligne trop chargée passe la fin (montant, boutons) dessous, à droite. */}
              <div className="flex items-center gap-3 min-w-0 flex-1 max-md:basis-full md:min-w-[16rem]">
                <FileText className="h-5 w-5 text-accent shrink-0" />
                <div>
                  <div className="font-mono font-medium whitespace-nowrap">{d.number}</div>
                  <div className="text-sm text-muted-foreground">{nameOf(d.client_id)} · {fmtDate(d.created_at)}{d.type === "quote" && quoteValidUntil(d) && <span data-testid={`quote-valid-${d.id}`}> · valable jusqu'au {frDay(quoteValidUntil(d))}</span>}</div>
                </div>
              </div>
              <span className={cn("px-2.5 py-1 text-xs font-medium rounded-full", DOC_STATUS[d.status]?.cls)} data-testid={`doc-status-${d.id}`}>{DOC_STATUS[d.status]?.label}</span>
              {d.subscription_id && <span className="px-2.5 py-1 text-xs font-medium rounded-full text-violet-700 bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300" title="Facture d'un abonnement : elle ne se supprime qu'en annulant l'abonnement (onglet Abonnements)" data-testid={`invoice-subscription-${d.id}`}>Abonnement</span>}
              {d.fuel_recharge_id && <span className="px-2.5 py-1 text-xs font-medium rounded-full text-sky-700 bg-sky-100 dark:bg-sky-900/30 dark:text-sky-300" title="Facture d'une recharge du compte carburant : elle ne se supprime qu'en annulant la recharge (onglet Compte carburant)" data-testid={`invoice-fuel-${d.id}`}>Carburant</span>}
              {d.type === "quote" && d.status !== "paid" && quoteValidUntil(d) && quoteValidUntil(d) < isoDay(new Date()) && <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground" data-testid={`quote-expired-${d.id}`}>Expiré</span>}
              {d.type === "invoice" && d.status === "sent" && <span className="text-[10px] uppercase tracking-overline text-amber-600 flex items-center gap-1" data-testid={`unpaid-flag-${d.id}`}>Impayée</span>}
              {d.type === "invoice" && d.status === "paid" && d.paid_at && <span className="text-[10px] uppercase tracking-overline text-emerald-600 flex items-center gap-1" data-testid={`paid-at-${d.id}`}>Payée le {fmtDate(d.paid_at)}{d.payment_method ? ` · ${paymentLabel(d.payment_method)}` : ""}</span>}
              {d.type === "invoice" && d.status === "paid" && <button type="button" className="text-[11px] text-muted-foreground underline hover:text-accent" onClick={() => setPayDialog({ open: true, item: d, edit: true })} data-testid={`edit-method-${d.id}`}>Modifier le mode</button>}
              <div className="text-right md:ml-auto"><div className="font-display font-bold">{money(d.total_ttc, cur)}</div>{!(franchise && !(d.total_tva > 0)) && <div className="text-xs text-muted-foreground">HT {money(d.total_ht, cur)} · TVA {money(d.total_tva, cur)}</div>}{d.total_debours > 0 && <div className="text-xs text-muted-foreground" data-testid={`invoice-debours-${d.id}`}>dont débours {money(d.total_debours, cur)}</div>}</div>
              <div className="flex items-center gap-1 md:ml-auto">
                <Button size="icon" variant="ghost" onClick={() => pdf(d.id)} title="Voir le PDF" data-testid={`invoice-pdf-${d.id}`}><Download className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => send(d.id)} title="Envoyer email" data-testid={`invoice-send-${d.id}`}><Send className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => waSend(d)} title="WhatsApp" data-testid={`invoice-wa-${d.id}`}><MessageCircle className="h-4 w-4 text-emerald-600" /></Button>
                {d.type === "invoice" && d.status === "sent" && <Button size="icon" variant="ghost" onClick={() => remind(d.id)} title="Relancer" data-testid={`invoice-remind-${d.id}`}><BellRing className="h-4 w-4 text-amber-600" /></Button>}
                {d.status !== "paid" && <Button size="icon" variant="ghost" onClick={() => setPayDialog({ open: true, item: d })} title="Marquer payé" data-testid={`invoice-paid-${d.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>}
                {d.type === "quote" && <Button size="sm" variant="outline" onClick={() => convert(d.id)} data-testid={`invoice-convert-${d.id}`}>→ Facture</Button>}
                {d.status === "draft" && !d.subscription_id && !d.fuel_recharge_id && <Button size="icon" variant="ghost" onClick={() => setToDelete(d)} title="Supprimer" data-testid={`invoice-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateDialog open={dialog} setOpen={setDialog} clients={clients} missions={missions} docs={docs} templates={templates} settings={settings} onSaved={load} />
      <PayDialog state={payDialog} setState={setPayDialog} onConfirm={markPaid} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{toDelete?.type === "quote" ? "Supprimer le devis" : "Supprimer le brouillon"} {toDelete?.number} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Le numéro <b>{toDelete?.number}</b> ne sera pas réutilisé : il manquera dans la suite de vos {toDelete?.type === "quote" ? "devis" : "factures"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => del(toDelete.id)} data-testid="delete-confirm-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!waConfirm} onOpenChange={(o) => { if (!o) setWaConfirm(null); }}>
        <AlertDialogContent data-testid="wa-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Envoyé par WhatsApp ?</AlertDialogTitle>
            <AlertDialogDescription>
              {waConfirm?.type === "invoice"
                ? <>Avez-vous bien envoyé la facture <b>{waConfirm?.number}</b> par WhatsApp ? Elle passera en « Envoyée » et ne sera plus modifiable ni supprimable.</>
                : <>Avez-vous bien envoyé le devis <b>{waConfirm?.number}</b> par WhatsApp ? Il passera en « Envoyé » et ne sera plus modifiable ni supprimable.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wa-confirm-no">Pas encore</AlertDialogCancel>
            <AlertDialogAction onClick={() => markSent(waConfirm)} data-testid="wa-confirm-yes">{waConfirm?.type === "invoice" ? "Oui, marquer envoyée" : "Oui, marquer envoyé"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PayDialog({ state, setState, onConfirm }) {
  const { settings } = useSettings();
  const [date, setDate] = useState("");
  const [method, setMethod] = useState(""); // volontairement sans présélection : le choix est obligatoire
  const edit = !!state.edit; // facture déjà payée : on ne corrige que le mode, la date est figée
  useEffect(() => {
    if (!state.open) return;
    const it = state.item || {};
    // même repli que le backend (invoice_paid_date) : la date effective de la rentrée ne bouge pas
    setDate(edit ? (it.paid_at || it.sent_at || it.created_at || "").slice(0, 10) : todayIn(settings?.timezone));
    setMethod(edit ? (it.payment_method || "") : "");
  }, [state.open, state.item, state.edit]);
  const confirm = () => { onConfirm(state.item.id, date, method, edit); setState({ open: false, item: null }); };
  return (
    <Dialog open={state.open} onOpenChange={(o) => setState({ ...state, open: o })}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-display">{edit ? `Mode de paiement de ${state.item?.number}` : `Marquer ${state.item?.number} comme payée`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Date de paiement</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={edit} data-testid="pay-date" />
            {edit && <p className="text-xs text-muted-foreground">La date de paiement n'est pas modifiable.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Mode de paiement</Label>
            <Select value={method || undefined} onValueChange={setMethod}>
              <SelectTrigger data-testid="pay-method"><SelectValue placeholder="Choisir un mode" /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setState({ open: false, item: null })}>Annuler</Button><Button onClick={confirm} disabled={!method || !date} data-testid="pay-confirm">Confirmer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesManager({ templates, onChanged }) {
  const [dialog, setDialog] = useState({ open: false, item: null });
  const del = async (id) => { await api.delete(`/templates/${id}`); toast.success("Modèle supprimé"); onChanged(); };
  return (
    <div className="space-y-4">
      <Button variant="outline" className="gap-2" onClick={() => setDialog({ open: true, item: null })} data-testid="new-template-button"><Plus className="h-4 w-4" /> Nouveau modèle</Button>
      <div className="grid md:grid-cols-2 gap-4">
        {templates.length === 0 && <p className="text-sm text-muted-foreground">Aucun modèle. Créez-en un pour personnaliser vos documents (en-tête, conditions, couleur).</p>}
        {templates.map((t) => (
          <div key={t.id} className="bg-card border border-border rounded-2xl shadow-card overflow-hidden p-5" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="h-6 w-6" style={{ background: `hsl(${t.accent_color || "43 65% 55%"})` }} />
                <div className="font-display font-bold">{t.name}</div>
                {t.is_default && <span className="text-xs text-accent flex items-center gap-1"><Star className="h-3 w-3 fill-accent" /> défaut</span>}
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, item: t })} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            {t.header && <p className="text-sm text-muted-foreground mt-2">{t.header}</p>}
            {t.terms && <p className="text-xs text-muted-foreground mt-2 border-t border-border pt-2">{t.terms}</p>}
          </div>
        ))}
      </div>
      <TemplateDialog state={dialog} setState={setDialog} onSaved={onChanged} />
    </div>
  );
}

function TemplateDialog({ state, setState, onSaved }) {
  const voc = useVocab();
  const empty = { name: "", header: "", footer: "", terms: "", accent_color: "43 65% 55%", logo_url: "", is_default: false };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const logoRef = React.useRef(null);
  useEffect(() => { setForm(state.item ? { ...empty, ...state.item } : empty); }, [state.open, state.item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", data.path); toast.success("Logo téléversé");
    } catch { toast.error("Échec du téléversement"); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    try {
      if (state.item) await api.patch(`/templates/${state.item.id}`, form); else await api.post("/templates", form);
      toast.success("Modèle enregistré"); onSaved(); setState({ open: false, item: null });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={state.open} onOpenChange={(o) => setState({ ...state, open: o })}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{state.item ? "Modifier le modèle" : "Nouveau modèle"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 border border-border">
            <div className="h-16 w-16 rounded-xl bg-card border border-border grid place-items-center overflow-hidden shrink-0">
              {form.logo_url ? <ChatImage path={form.logo_url} /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="flex-1">
              <Label className="text-sm font-semibold">Logo du document</Label>
              <p className="text-xs text-muted-foreground mb-2">Apparaît en haut de la facture / du devis.</p>
              <input type="file" accept="image/*" ref={logoRef} onChange={uploadLogo} className="hidden" data-testid="template-logo-input" />
              <Button type="button" size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="template-logo-upload">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {form.logo_url ? "Remplacer" : "Téléverser"}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Nom du modèle</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="template-name" placeholder="Ex. Facture standard" /></div>
          <div className="space-y-1.5"><Label>En-tête (sous le nom de l'entreprise)</Label><Input value={form.header} onChange={(e) => set("header", e.target.value)} data-testid="template-header" placeholder={voc.templateHeaderPlaceholder} /></div>
          <div className="space-y-1.5"><Label>Conditions de paiement</Label><Textarea value={form.terms} onChange={(e) => set("terms", e.target.value)} data-testid="template-terms" placeholder="Paiement à réception. Pénalités de retard : 3× le taux légal." /></div>
          <div className="space-y-1.5"><Label>Pied de page</Label><Input value={form.footer} onChange={(e) => set("footer", e.target.value)} data-testid="template-footer" placeholder="Merci de votre confiance" /></div>
          <div className="space-y-1.5"><Label>Couleur d'accent</Label>
            <div className="flex items-center gap-2">
              <span className="h-10 w-10 rounded-lg border border-border shrink-0" style={{ background: `hsl(${form.accent_color})` }} />
              <Input value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} data-testid="template-accent" placeholder="43 65% 55%" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer p-3 rounded-xl bg-muted/50"><Checkbox checked={form.is_default} onCheckedChange={(v) => set("is_default", !!v)} data-testid="template-default" /> Utiliser comme modèle par défaut</label>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setState({ open: false, item: null })}>Annuler</Button><Button className="rounded-full" onClick={save} data-testid="template-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, setOpen, clients, missions, docs, templates, settings, onSaved }) {
  const voc = useVocab();
  const transport = voc.business === "transport"; // ligne d'une course : trajet et passager (lot T1a)
  const franchise = settings?.vat_regime !== "assujetti"; // franchise : aucune notion de TVA à l'écran
  const vat = franchise ? 0 : (settings?.default_vat_rate ?? 0.2);
  const [type, setType] = useState("invoice");
  const [clientId, setClientId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState([{ label: "", qty: 1, unit_price: 0, vat_rate: vat }]);
  // Devis : « Valable jusqu'au » pré-rempli à aujourd'hui + 30 jours ; facture : « Échéance » vide.
  const changeType = (t) => { setType(t); setDueDate(t === "quote" ? addDays(isoDay(new Date()), QUOTE_VALIDITY_DAYS) : ""); };

  useEffect(() => { if (open) { setType("invoice"); setClientId(""); setTemplateId(""); setDueDate(""); setLines([{ label: "", qty: 1, unit_price: 0, vat_rate: vat }]); } }, [open]);
  // Transport (lot T6) : taux de TVA des courses (Réglages → Prix des courses), relus à chaque ouverture de la fenêtre.
  // Tant qu'ils ne sont pas arrivés, une course ne s'ajoute pas (bouton grisé) : une ligne ne part jamais avec un taux
  // deviné. Une réponse arrivée après la fermeture (ou d'une ouverture précédente) est ignorée.
  const [full, setFull] = useState(null);
  const [fullError, setFullError] = useState(false);
  useEffect(() => {
    if (!open || !transport) return undefined;
    let actif = true;
    setFull(null); setFullError(false);
    api.get("/settings/full").then((r) => { if (actif) setFull(r.data); }).catch(() => { if (actif) setFullError(true); });
    return () => { actif = false; };
  }, [open, transport]);
  const ratesReady = !transport || full !== null;

  // Une mission déjà sur une facture (brouillon, envoyée ou payée) n'est plus proposée, pour une facture
  // comme pour un devis. Une mission présente seulement sur un devis reste proposée.
  // Une mission couverte par un abonnement (même prêté par un autre client) n'est jamais proposée.
  const invoiced = new Set(docs.filter((d) => d.type === "invoice").flatMap((d) => d.mission_ids || []));
  const completedMissions = missions.filter((m) => m.client_id === clientId && m.status === "completed" && !invoiced.has(m.id) && !m.subscription_id);
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { label: "", qty: 1, unit_price: 0, vat_rate: vat }]);
  // Les lignes ajoutées depuis une mission gardent son id : il part dans mission_ids à l'enregistrement.
  const isAdded = (m) => lines.some((l) => l.mission_id === m.id);
  const addMissionLine = (m) => setLines((ls) => {
    if (ls.some((l) => l.mission_id === m.id)) return ls; // déjà ajoutée : pas de doublon
    if (!ratesReady) return ls; // transport : taux de TVA pas encore arrivés (lot T6)
    const kept = ls.filter((l) => (l.label && l.label.trim()) || parseFloat(l.unit_price) > 0);
    const added = [transport
      // Course (lots T1a, T6) : date et heure à Monaco, taux de TVA du type de course (10 % transfert, 20 % mise à disposition).
      ? { label: rideLineLabel(m, settings?.timezone), qty: 1, unit_price: m.price_ttc, vat_rate: rideVatRate(full, m), mission_id: m.id }
      : { label: `${missionLabel(m)} — ${fmtDate(m.start)}`, qty: 1, unit_price: m.price_ttc, vat_rate: vat, mission_id: m.id }];
    if (m.home_pickup) added.push({ label: "Récupération à domicile", qty: 1, unit_price: settings?.home_pickup_fee ?? 20, vat_rate: vat, mission_id: m.id });
    return [...kept, ...added];
  });
  const rmLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const r2 = (x) => Math.round(x * 100) / 100;
  const totals = lines.reduce((acc, l) => { const rate = parseFloat(l.vat_rate) || 0; const lineTtc = r2((parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0)); const lineHt = r2(lineTtc / (1 + rate)); acc.ht += lineHt; acc.tva += r2(lineTtc - lineHt); acc.ttc += lineTtc; return acc; }, { ht: 0, tva: 0, ttc: 0 });

  const save = async () => {
    if (!clientId) { toast.error("Sélectionnez un client"); return; }
    const cleanLines = lines.map((l) => ({ ...l, qty: parseFloat(l.qty) || 0, unit_price: parseFloat(l.unit_price) || 0, vat_rate: franchise ? 0 : (parseFloat(l.vat_rate) || 0) })).filter((l) => (l.label && l.label.trim()) || l.unit_price > 0);
    if (cleanLines.length === 0) { toast.error(`Ajoutez au moins une ligne ou une ${voc.mission}`); return; }
    // Missions encore présentes dans les lignes (une ligne supprimée délie sa mission).
    const missionIds = [...new Set(cleanLines.map((l) => l.mission_id).filter(Boolean))];
    const payload = { type, client_id: clientId, template_id: templateId, due_date: dueDate, mission_ids: missionIds,
      lines: cleanLines.map(({ mission_id, ...l }) => l) };
    try { await api.post("/invoices", payload); toast.success("Document créé"); onSaved(); setOpen(false); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Nouveau document</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={type} onValueChange={changeType}><SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="invoice">Facture</SelectItem><SelectItem value="quote">Devis</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Client</Label>
              <Select value={clientId} onValueChange={setClientId}><SelectTrigger data-testid="doc-client"><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Modèle</Label>
              <Select value={templateId || "none"} onValueChange={(v) => setTemplateId(v === "none" ? "" : v)}><SelectTrigger data-testid="doc-template"><SelectValue placeholder="Défaut" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Par défaut</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label data-testid="doc-due-date-label">{type === "quote" ? "Valable jusqu'au" : "Échéance"}</Label>
              <Input type="date" value={dueDate} min={type === "quote" ? isoDay(new Date()) : undefined} onChange={(e) => setDueDate(e.target.value)} data-testid="doc-due-date" /></div>
          </div>

          {clientId && completedMissions.length > 0 && (
            <div className="border border-border p-3">
              <Label className="text-xs uppercase tracking-overline text-muted-foreground">{`${voc.Missions} terminées — cliquer pour ajouter`}</Label>
              {!ratesReady && (
                <p className={cn("mt-1 text-xs", fullError ? "text-destructive" : "text-muted-foreground")} data-testid="ride-rates-wait">
                  {fullError ? "Taux de TVA des courses introuvables : fermez puis rouvrez la fenêtre." : "Chargement des taux de TVA…"}
                </p>
              )}
              <div className="mt-2 space-y-2">
                {completedMissions.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`sel-mission-${m.id}`}>
                    <span>{missionLabel(m)} · {fmtDate(m.start)} · {money(m.price_ttc, settings?.currency)}{m.home_pickup ? " + domicile" : ""}{m.ride_step === "no_show" ? " · Client absent" : ""}</span>
                    {isAdded(m)
                      ? <Button size="sm" variant="outline" className="gap-1 rounded-full" disabled data-testid={`sel-mission-add-${m.id}`}><Check className="h-3.5 w-3.5" /> Ajoutée</Button>
                      : <Button size="sm" variant="outline" className="gap-1 rounded-full" disabled={!ratesReady} onClick={() => addMissionLine(m)} data-testid={`sel-mission-add-${m.id}`}><Plus className="h-3.5 w-3.5" /> Ajouter</Button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {true && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-overline text-muted-foreground">Lignes manuelles</Label>
              {lines.map((l, i) => (
                // Sur téléphone : désignation sur sa propre ligne, puis Qté, Prix (et TVA) assez larges pour être lus.
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className={franchise ? "col-span-7 max-md:col-span-12" : "col-span-5 max-md:col-span-12"} placeholder="Désignation" value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} data-testid={`line-label-${i}`} />
                  <Input className="col-span-2 max-md:col-span-3" type="number" placeholder="Qté" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`line-qty-${i}`} />
                  <Input className={franchise ? "col-span-2 max-md:col-span-8" : "col-span-2 max-md:col-span-4"} type="number" placeholder={franchise ? "Prix" : "PU TTC"} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`line-price-${i}`} />
                  {!franchise && <Select value={String(l.vat_rate)} onValueChange={(v) => setLine(i, "vat_rate", v)}><SelectTrigger className="col-span-2 max-md:col-span-4" data-testid={`line-vat-${i}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0.2">TVA 20 %</SelectItem><SelectItem value="0.1">TVA 10 %</SelectItem><SelectItem value="0.055">TVA 5,5 %</SelectItem><SelectItem value="0">0 % · carburant / débours</SelectItem></SelectContent></Select>}
                  {/* Zone de clic : toute la hauteur de la ligne (h-9) et 4 px de plus de chaque côté (-mx-1) ;
                      pl-1 compense le décalage : l'icône reste exactement au même endroit. */}
                  <button className="col-span-1 h-9 -mx-1 pl-1 text-muted-foreground" onClick={() => rmLine(i)} title="Retirer la ligne" data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="gap-1" onClick={addLine} data-testid="add-line"><Plus className="h-3.5 w-3.5" /> Ligne</Button>
              {franchise
                ? <div className="text-right text-sm text-muted-foreground pt-2" data-testid="doc-total"><b className="text-foreground">Total {money(totals.ttc, settings?.currency)}</b></div>
                : <div className="text-right text-sm text-muted-foreground pt-2" data-testid="doc-total">HT {money(totals.ht, settings?.currency)} · TVA {money(totals.tva, settings?.currency)} · <b className="text-foreground">TTC {money(totals.ttc, settings?.currency)}</b></div>}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={save} data-testid="doc-save">Créer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
